// estate_model.js — the arithmetic behind the estate / warehouse view.
//
// Deliberately pure: no React, no fetching, no formatting. Everything here is
// a function of its arguments, which is what makes it testable and what lets
// the same numbers back both the panels and the drill-down lists without the
// two drifting apart.
//
// THE ONE RULE THAT HOLDS THE SCREEN TOGETHER
//
//   certain excess = what can move to another branch + what can go to the warehouse
//
// Every spare pack is in exactly one of the two panels. If that ever stops
// being true the screen is lying, so assertInvariant() below is exported and
// the test suite checks it on every scenario.

/* ---------------------------------------------------------------------------
   Naming
   --------------------------------------------------------------------------- */

// Same normalisation the excess page uses. Brand and generic collapse onto one
// key so that a branch holding "Atorvastatin 20mg tablets" can supply a branch
// whose usage file calls it something slightly different.
export function normaliseDrug(raw) {
  if (!raw) return { key: "" };
  let str = String(raw).toLowerCase();
  const strengthMatch = str.match(/\d+\s*(mg|g|ml|mcg)/);
  const strength = strengthMatch ? strengthMatch[0].replace(/\s+/g, "") : "";
  str = str
    .replace(/\(.*?\)/g, "")
    .replace(/\d+\s*(mg|g|ml|mcg)/g, "")
    .replace(/\d+\s*(tablet|capsule)/g, "")
    .replace(/tablets?|capsules?/g, "")
    .replace(/\s+/g, " ")
    .trim();
  return { key: `${str}_${strength}` };
}

/* ---------------------------------------------------------------------------
   Usage confidence

   Two things make a usage figure trustworthy, and counting months only covers
   one of them. Three months of 300, 20, 400 is three months of data and still
   worthless as a forecast. So confidence is months present AND how much the
   months agree with each other, measured as coefficient of variation.
   --------------------------------------------------------------------------- */

// Tuned against real shapes rather than picked from the air: 100/110/95 is
// steady, 100/200/50 is uneven but usable, 300/20/400 is noise wearing three
// months as a disguise and has to land in "low".
export const CV_STEADY = 0.35;
export const CV_ERRATIC = 0.60;

export function usageConfidence(monthly) {
  // A ZERO MONTH IS DATA. Dispensing none of something in May is an
  // observation about that product, not a gap in the record. Counting only
  // non-zero months made every slow mover look like it had one month of
  // history - exactly backwards, since a line that moves once a quarter is the
  // case we know most about, and slow movers are where excess collects.
  const months = (monthly || []).filter((m) => m && m.qty != null).map((m) => m.qty || 0);
  const n = months.length;
  if (n === 0) return { level: "none", months: 0, cv: null, why: "no monthly usage recorded" };

  const mean = months.reduce((a, b) => a + b, 0) / n;
  if (mean === 0) return { level: "none", months: n, cv: null, why: "no usage in any month" };

  const variance = months.reduce((a, b) => a + (b - mean) ** 2, 0) / n;
  const cv = Math.sqrt(variance) / mean;

  if (n === 1) return { level: "low", months: n, cv, why: "only one month of history" };
  if (cv > CV_ERRATIC) return { level: "low", months: n, cv, why: "usage swings widely month to month" };
  if (n >= 3 && cv <= CV_STEADY) return { level: "high", months: n, cv, why: "steady across three months" };
  return { level: "medium", months: n, cv, why: n >= 3 ? "three months but uneven" : "two months of history" };
}

/**
 * The worst rate actually observed.
 *
 * Confidence in the USAGE figure is not what matters - confidence in the
 * DECISION is, and for slow movers the two come apart. Folic acid at 0.3 packs
 * a month has dreadful statistics and an excess that is beyond argument: eight
 * packs against the worst month ever seen (one pack) is still eight months of
 * cover, so against a three-month target at least five packs are spare however
 * the noise falls.
 *
 * So excess is worked out twice - at the average, and at the worst observed
 * rate. What survives the worst case is spare for certain; the rest is held
 * back. Uncertainty becomes a QUANTITY rather than a verdict on a whole line.
 */
export function pessimisticUsage(monthly, mean, level) {
  const months = (monthly || []).filter((m) => m && m.qty != null).map((m) => m.qty || 0);
  if (!months.length) return mean * 1.5;
  const worst = Math.max(...months, mean);
  return level === "low" && months.length === 1 ? worst * 1.5 : worst;
}

/* ---------------------------------------------------------------------------
   Branch data freshness

   Separate question from confidence, and asked first: do we HAVE this branch's
   data, and is it current? Three months are needed, and they have to be the
   three that just finished. One month's grace is allowed for the lag in
   getting files in; beyond that the branch cannot be reasoned about at all.

   A month counts as held when the branch dispensed SOMETHING in it, anywhere
   in its range. That is deliberate: a real trading month always has activity,
   so a month sitting there as all-zero across every product is a month that
   was never uploaded, not a month with no dispensing. It makes the test
   immune to an API that pads absent months with zeros.
   --------------------------------------------------------------------------- */

/* ---------------------------------------------------------------------------
   Full months

   A month only earns a place in a usage average once it has genuinely been
   reported in full - not merely mentioned. The usage-uploads screen already
   answers "is this month full" for every branch: trading days against the
   estate's own typical count for that month, plus a partial flag carried
   from the import itself. This is that same test, reused rather than
   reinvented, so a month that shows a warning triangle on the uploads screen
   behaves the same way here rather than quietly being averaged in anyway.
   --------------------------------------------------------------------------- */

// Two days' grace absorbs a bank holiday and a branch that shuts one
// Saturday - the same allowance the uploads screen gives.
export const TRADING_DAYS_GRACE = 2;

export function isMonthFull(monthCov, typicalTradingDays) {
  if (!monthCov || !(monthCov.items > 0)) return false;
  if (monthCov.partial) return false;
  const short = Number.isFinite(monthCov.tradingDays)
    && Number.isFinite(typicalTradingDays)
    && monthCov.tradingDays < typicalTradingDays - TRADING_DAYS_GRACE;
  return !short;
}

/**
 * Chooses which of the fetched months one branch's usage should actually be
 * averaged over.
 *
 * monthsAsc is the full run fetched from the server, oldest first, and is
 * expected to be exactly one month longer than n - the one calendar month of
 * slack this function is allowed to use. The newest month is only taken once
 * it tests as full; where it does not - too few trading days against the
 * estate's typical count, marked partial by the importer, or nothing stored
 * for it at all yet - the window falls back to the n months before it
 * instead, and says so via `current: false`.
 *
 * Deliberately does not walk back more than the one month it was given
 * headroom for: a branch that is two or more months behind needs a human to
 * notice and chase it, not an average quietly reaching further and further
 * into the past to paper over the gap.
 */
export function chooseUsageWindow({
  monthsAsc = [],
  coverageByMonth = {},
  typicalByMonth = {},
  n = 3,
  graceOk = true,
}) {
  const N = monthsAsc.length;
  let end = N;
  while (end > n) {
    const ym = monthsAsc[end - 1];
    const cov = coverageByMonth[ym];
    // No coverage data for this month at all (the coverage call failed, or
    // this branch is missing from it) falls back to a plain calendar grace
    // period instead - see graceOk at the call site.
    const full = cov ? isMonthFull(cov, typicalByMonth[ym]) : graceOk;
    if (full) break;
    end -= 1;
  }
  const start = Math.max(0, end - n);
  return {
    months: monthsAsc.slice(start, end),
    // Whether the window reaches the newest month fetched at all - the one
    // fact the branch cards need to read "up to date" versus "one behind".
    current: end === N,
  };
}

export function monthAbbrev(ym) {
  const t = String(ym || "");
  if (t.length !== 6) return t;
  return new Date(Number(t.slice(0, 4)), Number(t.slice(4, 6)) - 1)
    .toLocaleString("en-GB", { month: "short" });
}

export function monthsLabel(monthsAsc = []) {
  return monthsAsc.map(monthAbbrev).join("/");
}

/**
 * The coverage-aware twin of assessBranches. Same green/amber/red vocabulary
 * and the same shape of result, but green now means "the newest month is
 * genuinely full", not just "some row exists for it" - which is what let a
 * handful of partially-uploaded days of a new month masquerade as a whole
 * one. Kept separate from assessBranches rather than replacing it: a caller
 * with no coverage data to hand still has the older, coarser test to fall
 * back on.
 */
export function assessBranchesByWindow({
  monthsAsc = [],
  coverageByPharmacy = {},   // pharmacyId -> { [month]: covRow }
  typicalByMonth = {},
  hasAnyUsage = new Set(),   // pharmacyId set - real dispensing seen anywhere in monthsAsc
  members = [],
  n = 3,
  graceOk = true,
}) {
  const result = new Map();
  const ids = new Set([...members.map((m) => m.pharmacyId), ...hasAnyUsage]);

  for (const pid of ids) {
    if (!hasAnyUsage.has(pid)) {
      result.set(pid, { status: "red", why: "no usage data at all", window: [] });
      continue;
    }
    const coverageByMonth = coverageByPharmacy[pid] || {};
    const win = chooseUsageWindow({ monthsAsc, coverageByMonth, typicalByMonth, n, graceOk });
    const label = monthsLabel(win.months);
    result.set(pid, {
      status: win.current ? "green" : "amber",
      why: win.current
        ? `up to date — ${label}`
        : `one month behind — ${label} (the newest month isn't fully reported yet)`,
      window: win.months,
    });
  }
  return result;
}

export function expectedMonths(now = new Date(), lag = 0) {
  const out = [];
  // Count back from the last COMPLETE month - the current one is still running
  // and will always look short.
  for (let i = 3 + lag; i >= 1 + lag; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    out.push(`${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, "0")}`);
  }
  return out;
}

export function assessBranches({ usageMeta = {}, usageMonthly = {}, members = [], now = new Date() }) {
  const held = new Map();
  for (const [key, meta] of Object.entries(usageMeta)) {
    const pid = meta.pharmacyId;
    if (!held.has(pid)) held.set(pid, new Set());
    for (const m of usageMonthly[key] || []) {
      if ((m.qty || 0) > 0) held.get(pid).add(String(m.month));
    }
  }

  const green = expectedMonths(now, 0);
  const amber = expectedMonths(now, 1);
  const label = (ms) => ms.map((m) => `${m.slice(4)}/${m.slice(2, 4)}`).join(", ");

  const result = new Map();
  const ids = new Set([...members.map((m) => m.pharmacyId), ...held.keys()]);

  for (const pid of ids) {
    const have = held.get(pid) || new Set();
    const monthsHeld = [...have].sort();

    if (green.every((m) => have.has(m))) {
      result.set(pid, { status: "green", monthsHeld, why: `up to date — ${label(green)}` });
    } else if (amber.every((m) => have.has(m))) {
      result.set(pid, { status: "amber", monthsHeld, why: `one month behind — ${label(amber)}` });
    } else if (!monthsHeld.length) {
      result.set(pid, { status: "red", monthsHeld, why: "no usage data at all" });
    } else {
      result.set(pid, {
        status: "red", monthsHeld,
        why: `needs ${label(green)} — only has ${label(monthsHeld.slice(-3))}`,
      });
    }
  }
  return result;
}

// A red branch takes no part in anything: not as a source, not as a
// destination, not in any total. A figure computed from stale data is worse
// than no figure, because it looks exactly like a good one.
export const isUsable = (status) => status === "green" || status === "amber";

export const isCertain = (level) => level === "high" || level === "medium";

/* ---------------------------------------------------------------------------
   Pending transfers — counted on BOTH sides

   The excess page only ever subtracted pending stock from the sender. That is
   right as far as it goes, but it leaves the receiver still looking short,
   which on this screen would be actively harmful: raise the transfers, and
   every destination stays in deficit and immediately attracts a second round
   of stock from somewhere else. So a pending transfer is treated as though it
   has already landed - out of the sender, into the receiver.
   --------------------------------------------------------------------------- */

// keyOf: how a transfer's drug name maps onto a row key. The generic basis
// normalises the name; the prescribed basis passes its own (2026-10).
export function buildPendingMap(transferData = [], keyOf = (drug) => normaliseDrug(drug).key) {
  const out = {};   // units leaving a branch
  const inb = {};   // units arriving at a branch

  for (const t of transferData) {
    if (t.status !== "pending") continue;
    for (const item of t.items || []) {
      const key = keyOf(item.drug);
      const units = (item.qty || 0) * (item.qtval || 1);
      if (t.fromPharmacyId) {
        const k = `${t.fromPharmacyId}_${key}`;
        out[k] = (out[k] || 0) + units;
      }
      // A warehouse-bound transfer has no destination pharmacy, so it leaves
      // the estate rather than arriving anywhere.
      if (t.toPharmacyId) {
        const k = `${t.toPharmacyId}_${key}`;
        inb[k] = (inb[k] || 0) + units;
      }
    }
  }
  return { out, in: inb };
}

// Units in, packs out, for anything shown to a human.
//
// Slow movers are why this is not just a round(): a line running at 0.3 packs
// a month has to say 0.3, not 0, or the sentence explaining a transfer starts
// by claiming the branch uses none of it. Above ten packs the decimal is
// noise, so it goes.
export function packStr(units, packSize) {
  const size = packSize > 0 ? packSize : 1;
  const packs = (units || 0) / size;
  if (packs > 0 && packs < 10) return packs.toFixed(1);
  return Math.round(packs).toLocaleString();
}

// Packs on the shelf are counted, not derived. Same rule the excess page uses:
// numberPackets where present, otherwise balance.
export function packetsOf(item) {
  const n = parseInt(item?.numberPackets, 10);
  if (Number.isFinite(n) && n !== 0) return n;
  const b = parseInt(item?.balance, 10);
  return Number.isFinite(b) ? b : 0;
}

/* ---------------------------------------------------------------------------
   The one balance calculation.

   Three places used to work out how much of a line is spare - this module, the
   excess table, and the Mongo aggregation behind the analysis - and each drifted
   from the others in its own direction: one counted pending stock on one side
   only, one applied the worst-month test and the others did not, one rounded a
   destination's need differently. Every disagreement the screens have shown so
   far has come out of that split.

   So it lives here once, as a pure function of numbers, and the callers pass
   their own figures in. Anything that wants to know what is spare asks this.
   --------------------------------------------------------------------------- */

export function computeBalance({
  qtyOnShelf = 0,
  pendingOut = 0,
  pendingIn = 0,
  usage = 0,
  monthly = [],
  packSize = 1,
  monthsAhead = 3,
}) {
  const size = packSize > 0 ? packSize : 1;
  const conf = usageConfidence(monthly);

  // Pending counted on BOTH sides - out of the sender, into the receiver.
  const adjustedQty = qtyOnShelf - (pendingOut || 0) + (pendingIn || 0);
  const need = (usage || 0) * monthsAhead;
  const balanceUnits = adjustedQty - need;

  // Floor keeps its sign on purpose: -50 units against a 28 pack is two packs
  // short, not one. Clamping the negative away is what once emptied the whole
  // shortage side of the engine.
  const wholePacks = Math.floor(balanceUnits / size);
  const excessUnitsSigned = wholePacks * size;

  const excessPacks = wholePacks > 0 ? wholePacks : 0;
  const deficitUnits = balanceUnits < 0 ? -balanceUnits : 0;
  // Rounded UP: part packs cannot be sent, and a branch short by 1.2 packs is
  // short by more than one.
  const deficitPacks = deficitUnits > 0 ? Math.max(1, Math.ceil(deficitUnits / size)) : 0;

  // The same sum again at the worst month actually recorded. What survives is
  // spare whatever the average turns out to be.
  const worstUsage = pessimisticUsage(monthly, usage || 0, conf.level);
  const worstBalance = adjustedQty - worstUsage * monthsAhead;
  const certainExcessPacks = conf.level === "none"
    ? 0
    : Math.max(0, Math.min(excessPacks, Math.floor(worstBalance / size)));

  return {
    packSize: size,
    adjustedQty,
    need,
    balanceUnits,
    excessUnitsSigned,
    excessPacks,
    deficitUnits,
    deficitPacks,
    certainExcessPacks,
    unsureExcessPacks: Math.max(0, excessPacks - certainExcessPacks),
    worstUsage,
    confidence: conf,
    monthsCover: usage > 0 ? adjustedQty / usage : null,
  };
}

/* ---------------------------------------------------------------------------
   Rows

   One row per branch per product, carrying everything the screen needs. Built
   from stock where stock exists, and from usage alone where a branch uses a
   product it holds none of - those lines are the deficits that make transfers
   worth doing at all.
   --------------------------------------------------------------------------- */

export function buildRows({
  inventoryData = [],
  usageLookup = {},      // `${pharmacyId}_${cleanName}` -> avg monthly units
  usageMeta = {},        // `${pharmacyId}_${cleanName}` -> { drugName, snomed, packSize, pharmacyId }
  usageMonthly = {},     // `${pharmacyId}_${cleanName}` -> [{ month, qty }]
  members = [],
  monthsAhead = 3,
  pending = { out: {}, in: {} },
  now = new Date(),
  freshness = null,      // pass one in to avoid recomputing it per render
  // Prescribed basis (2026-10): a branch whose prescribing data is current
  // and shows NO prescriptions for a product does not need that stock - a
  // known zero, not a missing figure - so its spare stock is certain.
  zeroUsageIsKnown = false,
}) {
  const nameOf = (id) => members.find((m) => m.pharmacyId === id)?.pharmacyName || id;
  const fresh = freshness || assessBranches({ usageMeta, usageMonthly, members, now });
  const rows = [];
  const seen = new Set();

  const finish = (r) => {
    const branch = fresh.get(r.pharmacyId) || { status: "red", why: "no usage data at all", monthsHeld: [] };

    // One calculation, shared with the excess table - see computeBalance above.
    const b = computeBalance({
      qtyOnShelf: r.qtyOnShelf,
      pendingOut: r.pendingOut,
      pendingIn: r.pendingIn,
      usage: r.usage,
      monthly: r.monthly,
      packSize: r.packSize,
      monthsAhead,
    });

    const knownZero = zeroUsageIsKnown && isUsable(branch.status) && !(r.usage > 0) && b.confidence.level === "none";
    if (knownZero) {
      b.certainExcessPacks = b.excessPacks;
      b.unsureExcessPacks = 0;
      b.confidence = { ...b.confidence, why: "nothing prescribed in the months counted" };
    }
    return {
      ...r,
      ...b,
      knownZero,
      // A stale branch releases nothing, whatever its own arithmetic says.
      certainExcessPacks: isUsable(branch.status) ? b.certainExcessPacks : 0,
      unsureExcessPacks: isUsable(branch.status) ? b.unsureExcessPacks : b.excessPacks,
      branchStatus: branch.status,
      branchWhy: branch.why,
      usable: isUsable(branch.status),
      certain: isCertain(b.confidence.level),
    };
  };

  // A stock document is one batch on one shelf, not one product. Folic acid at
  // one branch can be several documents in different pack sizes, so they are
  // grouped to a single line per branch per product first. Treating each
  // document as a row would count the same product several times and give it
  // a pack size belonging to only one of the batches.
  const grouped = new Map();

  for (const item of inventoryData) {
    // Only what is actually on a shelf. Ordered, in transit and written-off
    // stock is not available to move.
    if (!String(item.status || "").toLowerCase().includes("shelf")) continue;

    // estateKey/estateLabel: set by the prescribed basis (estate_prescribed.js),
    // which has already decided which prescription each pack belongs to.
    const parsed = item.estateKey ? { key: item.estateKey } : normaliseDrug(item.vmppNm);
    if (!parsed.key) continue;

    const pharmacyId = item.pharmacyId;
    const key = `${pharmacyId}_${parsed.key}`;

    const packets = packetsOf(item);
    // Defensive: qtval 0/missing means "no recorded pack size", not "zero
    // units on the shelf". Falling through to 0 here zeroes qtyOnShelf for
    // the whole grouped row below, which is exactly what makes a physically
    // scanned item disappear from every tab on this screen - it fails the
    // "qtyOnShelf > 0" test everywhere. 1 is the safe assumption: a
    // single-unit pack is still visible; 0 is not.
    const packSize = parseFloat(item.qtval) || 1;
    const units = packets * packSize;
    const value = packets * ((parseFloat(item.price) || 0) / 100);

    if (!grouped.has(key)) {
      grouped.set(key, {
        pharmacyId,
        pharmacyName: item.pharmacyName || nameOf(pharmacyId),
        drugName: item.estateLabel || item.vmppNm,
        cleanName: parsed.key,
        snomed: item.snomed,
        altKey: null,
        qtyOnShelf: 0, packets: 0, totalValue: 0,
        wholeUnits: 0, wholePackets: 0, splitPacks: 0,
        scannedQty: 0, assumedQty: 0, anySplit: false,
        // The individual shelf documents behind the total. Kept because the
        // grouped figure is what people query - "eight packs of what, where,
        // expiring when?" - and without these the only honest answer is to
        // send them to another screen.
        items: [],
        hasPrescribedUsage: item.hasPrescribedUsage,
        prescribedUnitsCovered: 0,
      });
    }
    const g = grouped.get(key);
    if (item.hasPrescribedUsage != null) g.hasPrescribedUsage = !!g.hasPrescribedUsage || item.hasPrescribedUsage;
    g.prescribedUnitsCovered += Number(item.prescribedUnitsCovered) || 0;
    if (item.altKey) g.altKey = item.altKey;
    g.qtyOnShelf += units;
    g.packets += packets;
    g.totalValue += value;
    g.items.push({
      packets, packSize, units, value,
      split: item.splitpack === true,
      fromInvoices: item.fromInvoices === true,
      location: item.location || null,
      expiryDate: item.expiryDate || null,
      firstScanned: item.firstScanned || null,
      status: item.status || null,
      // Carried straight from the shelf document rather than invented -
      // not every item has one, and a blank here just means it was never
      // recorded, same as location or expiry.
      batchNumber: item.batchNumber || null,
      serialNumber: item.serialNumber || null,
      // Which product is physically on the shelf (prescribed basis).
      brandName: item.brandName || null,
      genericName: item.genericName || null,
      stockDemandKeys: item.stockDemandKeys || null,
      prescribedUnitsCovered: item.prescribedUnitsCovered ?? null,
      supplier: item.supplier || null,
    });

    // A SPLIT PACK IS NOT A PACK SIZE. Its qtval records what is left in an
    // opened box, so folding it into the average drags the pack size below the
    // real one - and every units-to-packs conversion after it, on both sides of
    // a transfer, is then wrong. Split stock still counts as stock; it just
    // gets no vote on what a pack is.
    if (item.splitpack === true) {
      g.splitPacks += packets;
    } else {
      g.wholeUnits += units;
      g.wholePackets += packets;
    }

    // Stock physically counted versus stock only inferred from an incoming
    // invoice. Excess resting on unverified stock is the phantom-stock case,
    // so the split is carried through wherever the data provides it.
    if (item.scannedQty != null || item.assumedQty != null) {
      g.anySplit = true;
      g.scannedQty += Number(item.scannedQty) || 0;
      g.assumedQty += Number(item.assumedQty) || 0;
    } else if (item.fromInvoices) {
      g.anySplit = true;
      g.assumedQty += units;
    } else {
      g.scannedQty += units;
    }
  }

  for (const [key, g] of grouped) {
    seen.add(key);

    // The pack size to reason with is the one actually on that shelf, taken
    // from whole packs only: their units over their count. For a single pack
    // size that IS the size; for a mixed shelf it is the honest average. Where
    // a branch holds nothing but split stock there is no pack size to read, so
    // fall back to the usage document - and if even that has nothing, fall
    // back to 1 rather than 0. This packSize only affects HOW excess/deficit
    // packs are counted from here on; it plays no further part in whether
    // the row is visible (that was fixed at the qtyOnShelf source above), but
    // 0 would still wreck every packs-to-units conversion downstream of it.
    const packSize = g.wholePackets > 0 && g.wholeUnits > 0
      ? g.wholeUnits / g.wholePackets
      : (parseFloat(usageMeta[key]?.packSize) || 1);

    rows.push(finish({
      pharmacyId: g.pharmacyId,
      pharmacyName: g.pharmacyName,
      drugName: g.drugName,
      cleanName: g.cleanName,
      snomed: g.snomed || usageMeta[key]?.snomed,
      altKey: g.altKey || usageMeta[key]?.altKey || null,
      prescribedAs: usageMeta[key]?.prescribedAs || null,
      qtyOnShelf: g.qtyOnShelf,
      packetsOnShelf: g.packets,
      hasPrescribedUsage: g.hasPrescribedUsage,
      prescribedUnitsCovered: g.prescribedUnitsCovered,
      splitPacks: g.splitPacks,
      // Soonest expiry first: the reason to open this list is usually to find
      // out what is about to be wasted.
      items: g.items.sort((a, b) => String(a.expiryDate || "9999").localeCompare(String(b.expiryDate || "9999"))),
      packSizeFromShelf: g.wholePackets > 0,
      packSize,
      unitValue: g.qtyOnShelf > 0 ? g.totalValue / g.qtyOnShelf : 0,
      totalValue: g.totalValue,
      scannedQty: g.anySplit ? g.scannedQty : g.qtyOnShelf,
      assumedQty: g.anySplit ? g.assumedQty : 0,
      usage: usageLookup[key] || 0,
      monthly: usageMonthly[key] || [],
      pendingOut: pending.out[key] || 0,
      pendingIn: pending.in[key] || 0,
    }));
  }

  // Branches that use a product but hold none of it. Without these there are
  // no destinations, and every spare pack would look warehouse-bound.
  for (const [key, meta] of Object.entries(usageMeta)) {
    if (seen.has(key)) continue;
    rows.push(finish({
      pharmacyId: meta.pharmacyId,
      pharmacyName: nameOf(meta.pharmacyId),
      drugName: meta.drugName,
      cleanName: meta.cleanName,
      snomed: meta.snomed,
      altKey: meta.altKey || null,
      prescribedAs: meta.prescribedAs || null,
      qtyOnShelf: 0,
      packSize: meta.packSize || 0,
      unitValue: 0,
      totalValue: 0,
      scannedQty: 0,
      assumedQty: 0,
      noStock: true,
      usage: usageLookup[key] || 0,
      monthly: usageMonthly[key] || [],
      pendingOut: pending.out[key] || 0,
      pendingIn: pending.in[key] || 0,
    }));
  }

  return rows;
}

/* ---------------------------------------------------------------------------
   Matching

   For each product: everything certain and spare is offered to the branches
   that are short of it, largest need first, then whatever is left is offered
   to the warehouse.

   Largest-need-first rather than nearest-first is a deliberate choice. It is
   the rule that can be explained in one line on screen, and a rule nobody can
   restate is a rule nobody trusts. Distance is available if you would rather
   optimise mileage - see the note in the reply.
   --------------------------------------------------------------------------- */

// The cover period a row was built with, recovered from its own figures so the
// explanation does not need it passed in.
const monthsAheadOf = (r) => (r.usage > 0 ? Math.round(r.need / r.usage) : 0);

export function matchEstate(rows) {
  const byProduct = new Map();
  for (const r of rows) {
    if (!byProduct.has(r.cleanName)) byProduct.set(r.cleanName, []);
    byProduct.get(r.cleanName).push(r);
  }

  const transfers = [];
  const warehouse = [];
  const unsure = [];
  // Spare packs not placed with their own product's demand. Offered to the
  // row's altKey product next (prescribed basis: spare BRAND stock can fill
  // another branch's GENERIC prescription for the same medicine), then to the
  // warehouse. The generic basis sets no altKey, so this changes nothing there.
  const leftovers = [];
  const demandByProduct = new Map();

  // A stale branch is not a destination either. Shipping stock to a branch
  // on the strength of months-old data is the same mistake in reverse.
  //
  // Demand is carried in UNITS, not packs. A branch short of a product it
  // holds none of has no pack size of its own worth trusting - the packs it
  // will actually receive are the SENDER'S packs, so the conversion belongs
  // on the supply side. Converting on the destination's own figure was
  // producing a different answer from the detail screen for exactly this
  // case, and the detail screen was right.
  for (const [name, group] of byProduct) {
    demandByProduct.set(name, group
      .filter((r) => r.usable && r.deficitUnits > 0)
      .map((r) => ({ row: r, left: r.deficitUnits }))
      .sort((a, b) => b.left - a.left));
  }

  const allocate = (s, demand, viaAlt) => {
    const size = s.row.packSize > 0 ? s.row.packSize : 1;
    for (const d of demand) {
      if (s.left <= 0) break;
      if (d.left <= 0) continue;
      if (d.row.pharmacyId === s.row.pharmacyId) continue;
      // CEIL, matching the detail screen. Part packs cannot be sent, and a
      // branch short by 1.2 packs is short by more than one - rounding to
      // nearest sent 1 and left them short, needing a second trip for the
      // remainder. This is the one place the two screens still disagreed
      // after the pack-size fixes, so the frontend gives way to the rule the
      // backend already states and defends.
      const wanted = d.left > 0 ? Math.max(1, Math.ceil(d.left / size)) : 0;
      if (wanted <= 0) continue;
      const packs = Math.min(s.left, wanted);
      s.left -= packs;
      d.left -= packs * size;
      transfers.push({
        cleanName: s.row.cleanName,
        drugName: s.row.drugName,
        snomed: s.row.snomed,
        fromPharmacyId: s.row.pharmacyId,
        fromName: s.row.pharmacyName,
        toPharmacyId: d.row.pharmacyId,
        toName: d.row.pharmacyName,
        packs,
        packSize: s.row.packSize,
        value: packs * s.row.packSize * s.row.unitValue,
        // What the destination was actually short of, and what is left after
        // this leg. Shown in the list so a transfer that only part-covers a
        // branch says so rather than looking like the whole answer.
        // Both stated in the sender's packs, so the two columns are in the
        // same currency as the quantity being shipped.
        needPacks: Math.max(1, Math.ceil(d.row.deficitUnits / size)),
        shortAfter: d.left > 0 ? Math.ceil(d.left / size) : 0,
        // The working, shown on the row. Stated in PACKS throughout, and in
        // the SENDER'S packs, because a pack is what physically gets picked,
        // boxed and driven over. Nothing here is recalculated: the same unit
        // figures are simply divided by the pack size before they are shown.
        why: `${d.row.pharmacyName} uses ${packStr(d.row.usage, size)} packs/month `
           + `(${d.row.confidence.months} month${d.row.confidence.months === 1 ? "" : "s"} counted, `
           + `zeros included), so ${monthsAheadOf(d.row)} months needs `
           + `${packStr(d.row.need, size)} packs and it holds ${packStr(d.row.adjustedQty, size)}. `
           + `Short ${Math.max(1, Math.ceil(d.row.deficitUnits / size)).toLocaleString()} pack(s), `
           + `rounded up because part packs cannot be sent`
           + `${s.row.packSizeFromShelf ? "" : " (pack size estimated)"}.`
           + (viaAlt ? ` ${d.row.drugName} is prescribed generically there, so ${s.row.drugName} can fill it.` : ""),
        // Carried so the destination's own reliability is visible: sending
        // stock to a branch on the strength of one month is a weaker call
        // than sending it to one with three steady months.
        destConfidence: d.row.confidence.level,
        viaGeneric: !!viaAlt,
        fromRow: s.row,
        toRow: d.row,
      });
    }
  };

  for (const [name, group] of byProduct) {
    // Only packs that survive the worst observed usage, and only from branches
    // whose data is current enough to reason about at all.
    const supply = group
      .filter((r) => r.usable && r.certainExcessPacks > 0)
      .map((r) => ({ row: r, left: r.certainExcessPacks }))
      .sort((a, b) => b.left - a.left);

    // Everything spare that did not make that cut, with the reason.
    for (const r of group) {
      if (r.unsureExcessPacks > 0) {
        unsure.push({
          row: r, packs: r.unsureExcessPacks,
          value: r.unsureExcessPacks * r.packSize * r.unitValue,
          reason: !r.usable ? r.branchWhy
            : r.confidence.level === "none" ? "no usage recorded for this line"
            : `only ${r.certainExcessPacks} pack(s) survive the worst month seen (${r.confidence.why})`,
        });
      }
    }

    const demand = demandByProduct.get(name) || [];
    for (const s of supply) {
      allocate(s, demand, false);
      if (s.left > 0) leftovers.push(s);
    }
  }

  // Second pass: spare stock offered to the product it can ALSO fill.
  for (const s of leftovers) {
    const alt = s.row.altKey && demandByProduct.get(s.row.altKey);
    if (alt && alt.length) allocate(s, alt, true);
    if (s.left > 0) {
      warehouse.push({
        cleanName: s.row.cleanName,
        drugName: s.row.drugName,
        snomed: s.row.snomed,
        pharmacyId: s.row.pharmacyId,
        pharmacyName: s.row.pharmacyName,
        packs: s.left,
        packSize: s.row.packSize,
        value: s.left * s.row.packSize * s.row.unitValue,
        row: s.row,
      });
    }
  }

  const sum = (a, f) => a.reduce((t, x) => t + f(x), 0);
  return {
    transfers: transfers.sort((a, b) => b.value - a.value),
    warehouse: warehouse.sort((a, b) => b.value - a.value),
    unsure: unsure.sort((a, b) => b.value - a.value),
    totals: {
      transferPacks: sum(transfers, (t) => t.packs),
      transferValue: sum(transfers, (t) => t.value),
      warehousePacks: sum(warehouse, (w) => w.packs),
      warehouseValue: sum(warehouse, (w) => w.value),
      unsurePacks: sum(unsure, (u) => u.packs),
      unsureValue: sum(unsure, (u) => u.value),
    },
  };
}

// Every certain spare pack lands in exactly one panel. Exported so the tests
// can hold the screen to it on every scenario.
export function assertInvariant(rows, result) {
  const certainSpare = rows.reduce((t, r) => t + (r.usable ? r.certainExcessPacks : 0), 0);
  const placed = result.totals.transferPacks + result.totals.warehousePacks;
  return { certainSpare, placed, ok: certainSpare === placed };
}
// A compatible generic demand still counts even if other packs cover it all.
export function isStockWithoutUsage(row) {
  return row.usable && row.qtyOnShelf > 0 &&
    (row.hasPrescribedUsage == null ? !(row.usage > 0) : !row.hasPrescribedUsage);
}

// Preserve physical brands in the no-usage list rather than collapsing them
// under the generic label used by the demand/transfer calculation.
export function stockNoUsageDisplayRows(row) {
  if (!row.items?.length) return [row];
  const groups = new Map();
  for (const item of row.items) {
    const brand = item.brandName || row.drugName;
    const generic = item.genericName || row.drugName;
    const key = JSON.stringify([brand, generic]);
    if (!groups.has(key)) groups.set(key, { ...row, drugName: generic ? `${brand} (${generic})` : brand,
      stockDisplayKey: key, packetsOnShelf: 0, qtyOnShelf: 0, totalValue: 0, items: [] });
    const g = groups.get(key);
    g.packetsOnShelf += item.packets; g.qtyOnShelf += item.units; g.totalValue += item.value; g.items.push(item);
  }
  return [...groups.values()];
}

export function stockBrandLabel(row) {
  const labels = [...new Set((row.items || []).map(item => {
    const brand = item.brandName || row.drugName;
    const generic = item.genericName || row.drugName;
    return generic ? `${brand} (${generic})` : brand;
  }))];
  return labels.length ? labels.join('; ') : row.drugName;
}

// Shortfalls are shown in packets of the stock being offered, including when
// the receiving branch currently holds a different pack size.
export function stockRecipientNeeds(stock, allRows, monthsAhead) {
  const keys = new Set((stock.items || []).flatMap(item => item.stockDemandKeys || []));
  const hasEligibility = (stock.items || []).some(item => item.stockDemandKeys != null);
  const groups = new Map();
  for (const row of allRows || []) {
    if (row.pharmacyId === stock.pharmacyId || !row.usable || !(row.usage > 0)) continue;
    if (!(hasEligibility ? keys.has(row.cleanName) : row.cleanName === stock.cleanName || row.cleanName === stock.altKey)) continue;
    const balance = computeBalance({ qtyOnShelf: row.qtyOnShelf, pendingOut: row.pendingOut,
      pendingIn: row.pendingIn, usage: row.usage, monthly: row.monthly, packSize: row.packSize, monthsAhead });
    if (!(balance.deficitUnits > 0)) continue;
    const group = groups.get(row.pharmacyId) || { pharmacyId: row.pharmacyId, pharmacyName: row.pharmacyName, brandedUnits: 0, genericUnits: 0 };
    if (row.cleanName.startsWith('b:')) group.brandedUnits += balance.deficitUnits;
    else group.genericUnits += balance.deficitUnits;
    groups.set(row.pharmacyId, group);
  }
  const size = stock.packSize > 0 ? stock.packSize : 1;
  return [...groups.values()].map(g => ({ ...g, brandedPackets: Math.ceil(g.brandedUnits / size),
    genericPackets: Math.ceil(g.genericUnits / size), needed: Math.ceil(g.brandedUnits / size) + Math.ceil(g.genericUnits / size) }))
    .sort((a,b) => b.needed - a.needed);
}
